<template>
  <div>
    <page-header
      :page="pageHeaderConfig"
      right-side-class="col-sm-12 col-md-4 align-items-end d-flex pb-2 justify-content-end"
    >
      <template v-slot:customActions>
        <action-button
          class="mr-2 d-block"
          i="fas fa-arrow-circle-left"
          variant="success"
          @click="goToAnnouncementBoard()"
        >
          Regresar
        </action-button>
        <action-button
          v-if="hasPrintPermission"
          class="mr-2 d-block"
          i="fas fa-print"
          variant="outline-success"
          @click="onDownloadProposalProcessReport()"
        >
          Imprimir
        </action-button>
        <dropdown
          v-if="hasReportsPermission"
          class="reports-dropdown actions-dropdown"
          icon="fas fa-folders"
          :items="dropdownHeaderActions"
          responsive
          title="Informes"
        />
      </template>
    </page-header>

    <filter-manager
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :display-advanced="false"
      :filters="filtersConfig"
      @search="onSearchHandler"
      @service="() => {}"
    />
    <custom-separator class="my-4" text="Resultados" />
    <custom-result-not-found
      v-if="results.length == 0"
      text="No cuenta con información disponible."
    />
    <div v-for="row of results" :key="row.id">
      <result-row :data="row" />
    </div>
    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
//Services
import _generalApi from '@/modules/app/api';
import PageHeader from 'PageHeader';
import Dropdown from 'dropdown';
import ActionButton from 'ActionButton';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import { ResultRow } from './components';
import Pagination from 'pagination';

import {
  getInitFilters as _getInitFilters,
  getProposalRequests as _getProposalRequests,
  finalizeProposalProcess,
} from '@/modules/proposalProcess/api.js';

import {
  getSummaryEvaluationProcessReport,
  getEvaluatorProgressReport,
} from '@/modules/serviceProposalHistory/api';

import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';

//constants
import proposalArea from '@/utils/shared/constants/proposalArea.js';

const DEFAULT_PAGE_SIZE = 10;
const MODULE_NAME = 'ProposalProcessModule';

export default {
  name: 'ProposalProcessMain',
  components: {
    PageHeader,
    ActionButton,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    ResultRow,
    Pagination,
    Dropdown,
  },
  mixins: [alertMixin, storeMixin],
  data() {
    return {
      moduleName: MODULE_NAME,
      announcementNumber: '',
      isFinished: true,
      filterOptions: {},
      results: [],
      filterModel: {
        academicYear: null,
        requestStatus: null,
        proponentType: null,
        area: null,
        providerName: null,
        proposalOrAdviceNumber: null,
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    };
  },
  computed: {
    showFinishButton() {
      const isProvisionalRemedy =
        this.$route.params.areaId == proposalArea.ProvisionalRemedy;

      return isProvisionalRemedy && !this.isFinished && this.canEndProcess;
    },
    canEndProcess() {
      return this.userHasPermissions('rempro.providers.endproposalprocess');
    },
    getAnnouncementId() {
      const { id } = this.$route.params;
      return id;
    },
    academicYearId() {
      return this.$route.params.academicYearId;
    },
    pageHeaderConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: `Propuestas Sometidas [${this.announcementNumber}]`,
        routes: [
          'Proceso de Competencias',
          `Propuestas Sometidas [${this.announcementNumber}]`,
        ],
      };
    },
    dropdownHeaderActions() {
      return [
        {
          name: 'documents',
          label: 'Informe Resumen de Proceso de Evaluación de Propuesta',
          icon: 'fas fa-download text-green pr-1',
          visible: true,
          click: () => {
            this.downloadSummaryEvaluationProcessReport();
          },
        },
        {
          name: 'documentsProsesoEvaluador',
          label: 'Informe de Progreso Evaluadores',
          icon: 'fas fa-download text-green pr-1',
          visible: true,
          click: () => {
            this.downloadEvaluatorProgressReport();
          },
        },
      ];
    },
    hasPrintPermission() {
      return this.userHasPermissions('provider.proposal.process.download');
    },
    hasReportsPermission() {
      return this.userHasPermissions('provider.proposal.process.reports');
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    hasPersistInfo() {
      return Boolean(this.moduleData?.ProposalProcessModule || false);
    },
    filtersConfig() {
      return [
        {
          name: 'proponentType',
          component: 'CustomDropDown',
          options: this.getFilteredProponentTypes,
          placeholder: 'Tipo de Proponente',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
        },
        {
          name: 'providerName',
          component: 'CustomDropDown',
          options: this.filterOptions.providersName,
          placeholder: 'Nombre del Proponente',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
        },

        {
          name: 'requestStatus',
          component: 'CustomDropDown',
          options: this.getOrderedStatus,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
        },
        {
          name: 'proposalOrAdviceNumber',
          component: 'CustomInput',
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
          placeholder: 'Escriba Número de Propuesta',
          class: 'col-12 col-md-3',
        },
      ];
    },
    getOrderedStatus() {
      if (!this.filterOptions.requestStatus) return [];
      return this.filterOptions.requestStatus;
    },
    getFilteredProponentTypes() {
      if (!this.filterOptions.proponentTypes) return [];
      return this.filterOptions.proponentTypes;
    },
  },
  async mounted() {
    await this.setInitFilters();
    await this.onSearchHandler(this.filterModel);
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async finalizeProcess() {
      try {
        const confirmation = await this.ShowCancelModal(
          'Confirmación',
          '¿Estas seguro que desea finalizar el proceso para esta solicitud de propuesta?',
          'Si',
          'No'
        );

        if (!confirmation) return;

        await finalizeProposalProcess(this.getAnnouncementId);
        this.isFinished = true;

        this.ShowToast('', 'Proceso finalizado exitosamente', 'success');
      } catch (err) {
        const message = err?.response?.data.message ?? 'Error al finalizar';
        this.ShowToast(
          'Error al finalizar proceso evaluación',
          message,
          'error'
        );
      }
    },
    async setInitFilters() {
      const { data } = await _getInitFilters(this.getAnnouncementId);
      if (Object.values(data).length) {
        const { payload, collections } = data;
        this.filterOptions = { ...collections };
        this.announcementNumber = payload.announcementNumber;
        this.isFinished = payload.isFinished;
      }
    },
    async onSearchHandler(value, page = 1) {
      this.pagination.currentPage = page;

      const payload = {
        announcementId: this.getAnnouncementId,
        proposalOrAdviceNumber: value.proposalOrAdviceNumber,
        proponentTypeId: value.proponentType?.id,
        requestStatusId: value.requestStatus?.id,
        providerName: value.providerName?.name,
        providerId: value.providerName?.id,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };
      const { data: results } = await _getProposalRequests(payload);
      this.results = results.data;

      this.pagination = {
        ...this.pagination,
        totalItemCount: results.totalItemCount,
        pageCount: results.pageCount,
        currentPage: results.currentPage,
        count: results.count,
      };
    },
    async goToPageHandler(page) {
      await this.onSearchHandler(this.filterModel, page);
    },
    resetPagination() {
      this.pagination = {
        ...this.pagination,
        totalItemCount: 1,
        pageCount: 1,
        currentPage: 1,
        count: 1,
      };
    },
    async onDownloadProposalProcessReport() {
      if (this.getAnnouncementId) {
        await _generalApi.getReportsProposalProcess(this.getAnnouncementId);
      }
    },
    async downloadSummaryEvaluationProcessReport() {
      await getSummaryEvaluationProcessReport({
        academicYearId: this.academicYearId,
      }).then((response) => {
        _generalApi.ConvertToBlob(response.data);
      });
    },
    async downloadEvaluatorProgressReport() {
      await getEvaluatorProgressReport({
        academicYearId: this.academicYearId,
      }).then((response) => {
        _generalApi.ConvertToBlob(response.data);
      });
    },
    goToAnnouncementBoard() {
      this.$router.go(-1);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';

::v-deep .reports-dropdown {
  .btn {
    color: map-get($colors, 'green');
    font-weight: normal;
    font-size: 1rem;
    margin-right: 0.5rem;
    padding: 3px 10px;
  }
}
</style>
